'use client'

import * as React from 'react'
import { Primitive } from '@radix-ui/react-primitive'
import { Slot } from 'radix-ui'

import { cn } from '@/shared/lib/utils'

const ControlGroupContext = React.createContext<
	Pick<ControlGroupProps, 'orientation'>
>({
	orientation: 'horizontal',
})

function useControlGroup() {
	const context = React.use(ControlGroupContext)
	if (!context) {
		throw new Error('useControlGroup must be used within a <ControlGroup />.')
	}

	return context
}

export type ControlGroupProps = {
	orientation?: 'horizontal' | 'vertical'
} & React.ComponentProps<typeof Primitive.div>

function ControlGroup({
	className,
	orientation = 'horizontal',
	...props
}: ControlGroupProps) {
	return (
		<ControlGroupContext value={{ orientation }}>
			<Primitive.div
				data-slot='control-group'
				data-orientation={orientation}
				className={cn(
					'flex',
					orientation === 'vertical' && 'flex-col',
					className
				)}
				{...props}
			/>
		</ControlGroupContext>
	)
}

function ControlGroupItem({
	className,
	...props
}: React.ComponentProps<typeof Slot.Root>) {
	const { orientation } = useControlGroup()

	return (
		<Slot.Root
			data-slot='control-group-item'
			className={cn(
				'rounded-none focus-within:z-10',
				orientation === 'horizontal' &&
					'-me-px h-auto first:rounded-s-lg last:-me-0 last:rounded-e-lg',
				orientation === 'vertical' &&
					'[margin-block-end:-1px] w-auto first:rounded-ss-lg first:rounded-se-lg last:[margin-block-end:0] last:rounded-ee-lg last:rounded-es-lg',
				className
			)}
			{...props}
		/>
	)
}

export { ControlGroup, ControlGroupItem }
